import { useState, useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import SignatureCanvas from 'react-signature-canvas';
import { supabase } from '../lib/supabase';
import { collectivePaymentService } from '../services/collectivePayment.service';
import type {
  PaymentMode,
  GroupPaymentStatus,
  InstallmentInfo
} from '../types/collectivePayments';

interface CollectivePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  organizerId: string;
  onPaymentSubmitted: () => void;
}

export default function CollectivePaymentModal({
  isOpen,
  onClose,
  groupId,
  organizerId,
  onPaymentSubmitted,
}: CollectivePaymentModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<GroupPaymentStatus | null>(null);
  const [configuredMode, setConfiguredMode] = useState<PaymentMode | null>(null);
  
  // Estados para el modal de transferencia
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState<InstallmentInfo | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [transferDate, setTransferDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [isUploadingTransfer, setIsUploadingTransfer] = useState(false);
  
  // Estados para el modal de efectivo
  const [showCashModal, setShowCashModal] = useState(false);
  const [isSubmittingCash, setIsSubmittingCash] = useState(false);
  const [organizerDni, setOrganizerDni] = useState('');
  const signatureRef = useRef<SignatureCanvas>(null);

  const loadPaymentData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [modes, status] = await Promise.all([
        collectivePaymentService.getPaymentModes(),
        collectivePaymentService.getGroupPaymentStatus(groupId)
      ]);

      setPaymentStatus(status);

      const mode = modes.find(m => m.id === status?.config?.payment_mode_id);
      setConfiguredMode(mode || null);
    } catch (error: any) {
      const errorMessage = error.message || 'Error al cargar información de pagos';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  console.log(paymentStatus);

  useEffect(() => {
    if (isOpen) {
      loadPaymentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, groupId]);

  const handleOpenTransferModal = (installment: InstallmentInfo) => {
    setSelectedInstallment(installment);
    setSelectedFile(null);
    setTransferDate(new Date().toISOString().split('T')[0]);
    setShowTransferModal(true);
  };

  const handleOpenCashModal = (installment: InstallmentInfo) => {
    setSelectedInstallment(installment);
    setOrganizerDni('');
    setShowCashModal(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Tipo de archivo no permitido. Solo PDF, JPG, PNG.');
        e.target.value = '';
        return;
      }
      
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        toast.error('El archivo es demasiado grande. Máximo 20MB.');
        e.target.value = '';
        return;
      }
    }
    
    setSelectedFile(file);
  };

  const handleSubmitTransfer = async () => {
    if (!selectedFile || !selectedInstallment) return;

    setIsUploadingTransfer(true);
    const toastId = toast.loading('Procesando pago por transferencia...');

    try {
      // Subir archivo a Supabase Storage
      const filePath = await collectivePaymentService.uploadReceiptFile(
        selectedFile,
        groupId,
        supabase
      );

      // Enviar pago al backend
      await collectivePaymentService.submitPayment(groupId, {
        group_id: groupId,
        organizer_id: organizerId,
        installment_number: selectedInstallment.installment_number,
        amount: Number(selectedInstallment.expected_amount),
        is_cash: false,
        receipt_url: filePath,
        receipt_date: transferDate
      });

      toast.success('Pago registrado correctamente. Esperando validación.', { id: toastId });
      setShowTransferModal(false);
      setSelectedFile(null);

      // Recargar datos
      await loadPaymentData();
      onPaymentSubmitted();
    } catch (error: any) {
      toast.error(error.message || 'Error al procesar el pago', { id: toastId });
    } finally {
      setIsUploadingTransfer(false);
    }
  };

  const handleClearSignature = () => {
    signatureRef.current?.clear();
  };

  const validateDni = (dni: string): boolean => {
    const dniRegex = /^[0-9]{8}[A-Za-z]$/;
    return dniRegex.test(dni);
  };

  const handleSubmitCash = async () => {
    if (!signatureRef.current || !selectedInstallment) return;

    if (!organizerDni.trim()) {
      toast.error('Por favor, introduce tu DNI');
      return;
    }

    if (!validateDni(organizerDni)) {
      toast.error('DNI inválido. Debe tener 8 dígitos seguidos de una letra');
      return;
    }

    if (signatureRef.current.isEmpty()) {
      toast.error('Por favor, firma el documento antes de continuar');
      return;
    }

    setIsSubmittingCash(true);
    const toastId = toast.loading('Procesando pago en efectivo...');

    try {
      const signatureData = signatureRef.current.toDataURL('image/png');

      await collectivePaymentService.submitPayment(groupId, {
        group_id: groupId,
        organizer_id: organizerId,
        installment_number: selectedInstallment.installment_number,
        amount: Number(selectedInstallment.expected_amount),
        is_cash: true,
        organizer_signature_base64: signatureData,
        organizer_dni: organizerDni
      });

      toast.success('Pago registrado correctamente. Esperando validación.', { id: toastId });
      setShowCashModal(false);

      await loadPaymentData();
      onPaymentSubmitted();
    } catch (error: any) {
      toast.error(error.message || 'Error al procesar el pago', { id: toastId });
    } finally {
      setIsSubmittingCash(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-gray-500';
      case 'submitted': return 'text-blue-600';
      case 'validated': return 'text-green-600';
      case 'invalid': return 'text-red-600';
      default: return 'text-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'submitted': return 'Esperando validación';
      case 'validated': return 'Validado';
      case 'invalid': return 'Rechazado';
      default: return status;
    }
  };

  if (!isOpen) return null;

  return (
      <>
        {/* Modal principal de pagos colectivos */}
        <div className="fixed inset-0 z-[99999] overflow-hidden" role="dialog" aria-modal="true">
        <div className="flex items-center justify-center min-h-full p-4 text-center">
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
            onClick={onClose}
          />

          <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-4xl max-h-[85vh] flex flex-col">
            {/* Cabecera */}
            <div className="bg-white px-3 sm:px-4 md:px-6 pt-2 sm:pt-3 pb-2 flex-shrink-0 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <h3 className="text-xs sm:text-sm md:text-base font-bold text-gray-900 pr-2">
                  Pagos Colectivos
                </h3>
                <button
                  onClick={onClose}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 flex-shrink-0 -mt-1"
                >
                  <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contenido scrolleable */}
            <div className="overflow-y-auto flex-1 px-3 sm:px-4 md:px-6 py-2 sm:py-3">
              {isLoading ? (
                <div className="py-8 sm:py-12 text-center">
                  <div className="inline-block animate-spin rounded-full h-6 sm:h-8 w-6 sm:w-8 border-b-2 border-[#F90000]"></div>
                  <p className="mt-2 text-xs sm:text-sm text-gray-500">Cargando información de pagos...</p>
                </div>
              ) : error ? (
                <div className="py-6 sm:py-8 text-center">
                  <div className="text-red-500 text-2xl sm:text-3xl mb-2">⚠️</div>
                  <p className="text-xs sm:text-sm text-gray-900 font-medium mb-1">No se pudo cargar la información</p>
                  <p className="text-xs text-gray-600 mb-3 px-4">{error}</p>
                  <button
                    onClick={loadPaymentData}
                    className="px-3 py-1.5 bg-[#F90000] text-white text-xs rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Reintentar
                  </button>
                </div>
              ) : paymentStatus ? (
                <div>
                  {/* Progreso general */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-gray-700">Progreso</span>
                      <span className="text-sm sm:text-base font-bold text-blue-600">
                        {Number(paymentStatus.completion_percentage).toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                      <div
                        className="bg-blue-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Number(paymentStatus.completion_percentage)}%` }}
                      />
                    </div>
                    <div className="mt-1.5 space-y-0.5 text-xs text-gray-600">
                      <div className="flex justify-between">
                        <span>Pedidos pendientes</span>
                        <span>€{Number(paymentStatus.gross_amount).toFixed(2)}</span>
                      </div>
                      {(paymentStatus.organizer_benefit ?? 0) > 0 && (
                        <div className="flex justify-between">
                          <span>Tu comisión</span>
                          <span>−€{Number(paymentStatus.organizer_benefit ?? 0).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-200 pt-0.5">
                        <span>A liquidar</span>
                        <span>€{Number(paymentStatus.total_amount).toFixed(2)}</span>
                      </div>
                      {paymentStatus.total_validated > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Validado</span>
                          <span>€{Number(paymentStatus.total_validated).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Modo de pago configurado */}
                  {configuredMode && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
                      <p className="text-xs text-gray-500">Modo de pago</p>
                      <p className="text-xs sm:text-sm font-semibold text-gray-900">{configuredMode.name}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{configuredMode.description}</p>
                    </div>
                  )}

                  {/* Lista de plazos */}
                  <div className="space-y-1.5 sm:space-y-2">
                    {paymentStatus.installments.map((installment) => (
                      <div 
                        key={installment.installment_number}
                        className="border border-gray-200 rounded-lg p-2 sm:p-3 hover:shadow-md transition-shadow"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-1.5 mb-1">
                              <span className="text-xs sm:text-sm font-semibold text-gray-900">
                                Plazo {installment.installment_number}
                              </span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                {installment.percentage}%
                              </span>
                              <span className={`text-xs font-medium ${getStatusColor(installment.status)}`}>
                                {getStatusText(installment.status)}
                              </span>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-600">
                              <span className="font-bold text-[#F90000]">€{Number(installment.expected_amount).toFixed(2)}</span>
                            </p>
                            {installment.attempts_count > 1 && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                Intentos: {installment.attempts_count}
                              </p>
                            )}
                            {installment.current_payment?.invalidation_reason && (
                              <div className="mt-1.5 bg-red-50 border border-red-200 rounded p-2">
                                <div className="flex items-start gap-1.5">
                                  <svg className="h-3.5 w-3.5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                  </svg>
                                  <div className="flex-1">
                                    <p className="text-xs font-semibold text-red-800 mb-0.5">Pago Rechazado</p>
                                    <p className="text-xs text-red-700 leading-tight">{installment.current_payment.invalidation_reason}</p>
                                    {installment.current_payment.invalidated_at && (
                                      <p className="text-xs text-red-600 mt-1 opacity-75">
                                        {new Date(installment.current_payment.invalidated_at).toLocaleDateString('es-ES', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </p>
                                    )}
                                    <p className="text-xs text-gray-600 mt-1.5 bg-white rounded px-1.5 py-0.5 border border-red-100">
                                      Puedes enviar un nuevo comprobante
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Botones de acción */}
                          <div className="flex flex-col sm:flex-row gap-1.5 sm:ml-3 w-full sm:w-auto">
                            {(installment.status === 'pending' || installment.status === 'invalid') && (
                              <>
                                <button
                                  onClick={() => handleOpenTransferModal(installment)}
                                  className="px-2.5 py-1.5 bg-[#F90000] text-white text-xs font-medium rounded hover:bg-red-700 transition-colors whitespace-nowrap"
                                >
                                  Transferencia
                                </button>
                                {configuredMode?.allows_cash && (
                                  <button
                                    onClick={() => handleOpenCashModal(installment)}
                                    className="px-2.5 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors whitespace-nowrap"
                                  >
                                    Efectivo
                                  </button>
                                )}
                              </>
                            )}
                            {installment.status === 'submitted' && (
                              <span className="px-2.5 py-1.5 bg-blue-100 text-blue-800 text-xs font-medium rounded text-center">
                                Validando...
                              </span>
                            )}
                            {installment.status === 'validated' && (
                              <span className="px-2.5 py-1.5 bg-green-100 text-green-800 text-xs font-medium rounded flex items-center justify-center gap-1">
                                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                OK
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-8 sm:py-12 text-center">
                  <p className="text-sm sm:text-base text-gray-500">No se encontró configuración de pagos colectivos</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-3 sm:px-4 md:px-6 py-2 flex-shrink-0 border-t border-gray-200">
              <button
                onClick={onClose}
                className="w-full sm:w-auto sm:ml-auto block rounded-md border border-gray-300 shadow-sm px-3 py-1.5 bg-white text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de pago por transferencia */}
      {showTransferModal && selectedInstallment && (
        <div className="fixed inset-0 z-[100000] overflow-hidden" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-full p-4 text-center">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              onClick={() => setShowTransferModal(false)}
            />

            <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg max-h-[85vh] flex flex-col">
              {/* Cabecera */}
              <div className="bg-white px-3 sm:px-4 md:px-6 pt-2 sm:pt-3 pb-2 flex-shrink-0 border-b border-gray-200">
                <h3 className="text-xs sm:text-sm md:text-base font-bold text-gray-900">
                  Transferencia - Plazo {selectedInstallment.installment_number}
                </h3>
              </div>

              {/* Contenido scrolleable */}
              <div className="overflow-y-auto flex-1 px-3 sm:px-4 md:px-6 py-2 sm:py-3">

                <div className="space-y-2 sm:space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3">
                    <p className="text-xs font-medium text-gray-700">Importe:</p>
                    <p className="text-base sm:text-lg md:text-xl font-bold text-[#F90000]">€{Number(selectedInstallment.expected_amount).toFixed(2)}</p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Fecha de transferencia
                    </label>
                    <input
                      type="date"
                      value={transferDate}
                      onChange={(e) => setTransferDate(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs sm:text-sm focus:ring-2 focus:ring-[#F90000] focus:border-[#F90000]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Comprobante (PDF, JPG, PNG)
                    </label>
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept="application/pdf,image/jpeg,image/png"
                      className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-medium file:bg-red-50 file:text-[#F90000] hover:file:bg-red-100"
                    />
                    {selectedFile && (
                      <p className="mt-1.5 text-xs text-green-600 break-all">
                        ✓ {selectedFile.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-3 sm:px-4 md:px-6 py-2 flex-shrink-0 border-t border-gray-200">
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-1.5">
                  <button
                    onClick={() => setShowTransferModal(false)}
                    disabled={isUploadingTransfer}
                    className="w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-3 py-1.5 bg-white text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSubmitTransfer}
                    disabled={!selectedFile || isUploadingTransfer}
                    className="w-full sm:w-auto inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-3 py-1.5 bg-[#F90000] text-xs sm:text-sm font-medium text-white hover:bg-red-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isUploadingTransfer ? 'Procesando...' : 'Enviar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de pago en efectivo */}
      {showCashModal && selectedInstallment && (
        <div className="fixed inset-0 z-[100000] overflow-hidden" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-full p-4 text-center">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              onClick={() => setShowCashModal(false)}
            />

            <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg max-h-[85vh] flex flex-col">
              {/* Cabecera */}
              <div className="bg-white px-3 sm:px-4 md:px-6 pt-2 sm:pt-3 pb-2 flex-shrink-0 border-b border-gray-200">
                <h3 className="text-xs sm:text-sm md:text-base font-bold text-gray-900">
                  Efectivo - Plazo {selectedInstallment.installment_number}
                </h3>
              </div>

              {/* Contenido scrolleable */}
              <div className="overflow-y-auto flex-1 px-3 sm:px-4 md:px-6 py-2 sm:py-3">

                <div className="space-y-2 sm:space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3">
                    <p className="text-xs font-medium text-gray-700">Importe:</p>
                    <p className="text-base sm:text-lg md:text-xl font-bold text-[#F90000]">€{Number(selectedInstallment.expected_amount).toFixed(2)}</p>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 sm:p-3">
                    <p className="text-xs text-gray-800">
                      Declaro haber recibido <strong>€{Number(selectedInstallment.expected_amount).toFixed(2)}</strong> en efectivo del plazo {selectedInstallment.installment_number}.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Tu DNI <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={organizerDni}
                      onChange={(e) => setOrganizerDni(e.target.value.toUpperCase())}
                      placeholder="12345678A"
                      maxLength={9}
                      className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Formato: 8 dígitos + letra</p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Firma del organizador
                    </label>
                    <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                      <SignatureCanvas
                        ref={signatureRef}
                        canvasProps={{
                          className: 'w-full bg-white',
                          style: { 
                            touchAction: 'none',
                            height: window.innerWidth < 640 ? '100px' : '140px'
                          }
                        }}
                        backgroundColor="white"
                      />
                    </div>
                    <button
                      onClick={handleClearSignature}
                      className="mt-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      Limpiar
                    </button>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-3 sm:px-4 md:px-6 py-2 flex-shrink-0 border-t border-gray-200">
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-1.5">
                  <button
                    onClick={() => setShowCashModal(false)}
                    disabled={isSubmittingCash}
                    className="w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-3 py-1.5 bg-white text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSubmitCash}
                    disabled={isSubmittingCash}
                    className="w-full sm:w-auto inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-3 py-1.5 bg-green-600 text-xs sm:text-sm font-medium text-white hover:bg-green-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmittingCash ? 'Procesando...' : 'Confirmar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

